import {
  V2_SEARCH_DEFAULT_PAGE_SIZE,
  V2_SEARCH_DEFAULT_SORT,
  V2_SEARCH_PAGE_SIZES,
  V2_SEARCH_SORTS,
  type DateRange,
  type NumberRange,
  type V2SearchPageSize,
  type V2SearchRequest,
  type V2SearchSort,
} from "../api/v2Search";
import { parseJapaneseDate, yearsBefore } from "./japaneseDate";

/** Text inputs and selects. Ranges are kept as two strings (`…Min`/`…Max`, `…From`/`…To`). */
export const V2_SEARCH_TEXT_KEYS = [
  // 物件
  "location",
  "number",
  "realEstateNumber",
  "buildingName",
  "landAreaMin",
  "landAreaMax",
  "floorAreaMin",
  "floorAreaMax",
  "builtFrom",
  "builtTo",
  "ageMin",
  "ageMax",
  "floorsMin",
  "floorsMax",
  "closed",
  "oneBuildingFloorAreaMin",
  "oneBuildingFloorAreaMax",
  "unitFloorMin",
  "unitFloorMax",
  "hasAnnex",
  "annexFloorAreaMin",
  "annexFloorAreaMax",
  "titleOwnerName",
  "titleOwnerAddress",
  "siteLocation",
  // 所有者
  "ownerName",
  "ownerAddress",
  "ownerCorporation",
  "ownerNumber",
  "ownerCountMin",
  "ownerCountMax",
  "ownerExternalCharacter",
  // 甲区
  "kokuAcceptFrom",
  "kokuAcceptTo",
  "kokuReasonDateFrom",
  "kokuReasonDateTo",
  "kokuProvisional",
  "trustPartyName",
  // 担保権
  "securityExists",
  "holderName",
  "debtorName",
  "debtorAddress",
  "securityAcceptFrom",
  "securityAcceptTo",
  "amountMin",
  "amountMax",
  "rankMin",
  "rankMax",
  "handlingBranch",
  "securityProvisional",
  "principalFixed",
  "hasChanges",
  "jointUndetermined",
  "hasJointSecurity",
  "jointCatalogNumber",
  "jointItemLocation",
  "jointItemNumber",
  "usageRightHolderName",
  // 取得・依頼
  "acquiredFrom",
  "acquiredTo",
  "requestName",
  "requestId",
] as const;

/** Multi-select values (repeated query keys). */
export const V2_SEARCH_ARRAY_KEYS = [
  "kinds",
  "prefectures",
  "cities",
  "landCategories",
  "landAreaSources",
  "buildingTypes",
  "structures",
  "builtStatuses",
  "siteRightKinds",
  "ownerCautions",
  "restrictions",
  "kokuPurposeKinds",
  "kokuPurposeActions",
  "kokuReasons",
  "securityKinds",
  "otsukuReasons",
  "ownerStatuses",
  "securityStatuses",
] as const;

/** Checkboxes ("1" in the query when on). */
export const V2_SEARCH_FLAG_KEYS = [
  "locationHistory",
  "ownerHistory",
  "restrictionsHistory",
  "securityHistory",
  "mineOnly",
] as const;

export type V2SearchTextKey = (typeof V2_SEARCH_TEXT_KEYS)[number];
export type V2SearchArrayKey = (typeof V2_SEARCH_ARRAY_KEYS)[number];
export type V2SearchFlagKey = (typeof V2_SEARCH_FLAG_KEYS)[number];

export type V2SearchConditions = Record<V2SearchTextKey, string> &
  Record<V2SearchArrayKey, string[]> &
  Record<V2SearchFlagKey, boolean>;

/** Date inputs: accept 和暦 and are stored in the URL as "YYYY-MM-DD". */
export const DATE_RANGE_KEYS = [
  ["builtFrom", "builtTo"],
  ["kokuAcceptFrom", "kokuAcceptTo"],
  ["kokuReasonDateFrom", "kokuReasonDateTo"],
  ["securityAcceptFrom", "securityAcceptTo"],
  ["acquiredFrom", "acquiredTo"],
] as const satisfies ReadonlyArray<readonly [V2SearchTextKey, V2SearchTextKey]>;

/** Partial-match text; 1–2 characters are accepted only with another condition (contract, D9). */
export const PARTIAL_TEXT_KEYS = [
  "location",
  "buildingName",
  "titleOwnerName",
  "titleOwnerAddress",
  "siteLocation",
  "ownerName",
  "ownerAddress",
  "trustPartyName",
  "holderName",
  "debtorName",
  "debtorAddress",
  "handlingBranch",
  "jointItemLocation",
  "usageRightHolderName",
  "requestName",
] as const satisfies readonly V2SearchTextKey[];

/** Conditions that only make sense while a current security may exist. */
export const SECURITY_DETAIL_KEYS = [
  "holderName",
  "debtorName",
  "debtorAddress",
  "securityAcceptFrom",
  "securityAcceptTo",
  "amountMin",
  "amountMax",
  "rankMin",
  "rankMax",
  "handlingBranch",
  "securityProvisional",
  "principalFixed",
  "hasChanges",
  "jointUndetermined",
  "hasJointSecurity",
  "jointCatalogNumber",
  "jointItemLocation",
  "jointItemNumber",
  "securityKinds",
  "otsukuReasons",
  "securityHistory",
] as const satisfies ReadonlyArray<keyof V2SearchConditions>;

export const defaultV2SearchConditions = ((): V2SearchConditions => {
  const values: Record<string, string | string[] | boolean> = {};
  V2_SEARCH_TEXT_KEYS.forEach((key) => {
    values[key] = "";
  });
  V2_SEARCH_ARRAY_KEYS.forEach((key) => {
    values[key] = [];
  });
  V2_SEARCH_FLAG_KEYS.forEach((key) => {
    values[key] = false;
  });
  return values as V2SearchConditions;
})();

/** Result-list state kept in the URL next to the conditions. */
export interface V2SearchListState {
  sort: V2SearchSort;
  page: number;
  pageSize: V2SearchPageSize;
  onePerProperty: boolean;
}

export const LIST_STATE_KEYS = ["sort", "page", "pageSize", "onePerProperty"];

/**
 * 全角の数字・記号を半角にし、前後の空白を除く
 */
export const normalizeInput = (value: string): string =>
  value
    .replace(/[０-９．－，]/g, (c) =>
      String.fromCharCode(c.charCodeAt(0) - 0xfee0)
    )
    .replace(/,/g, "")
    .trim();

/** 数値の入力を読む。空は null、読めないときは NaN */
export const parseNumberInput = (value: string): number | null => {
  const normalized = normalizeInput(value);
  if (normalized === "") return null;
  return /^-?\d+(\.\d+)?$/.test(normalized) ? Number(normalized) : NaN;
};

/** 番号（不動産番号・法人番号など）の入力から数字だけを取り出す */
export const digitsOnly = (value: string): string =>
  normalizeInput(value).replace(/[-\s]/g, "");

/**
 * URLクエリから検索条件を復元する（検索条件の正本はURL）。未知のキーは捨てる
 */
export const paramsToConditions = (sp: URLSearchParams): V2SearchConditions => {
  const next: V2SearchConditions = { ...defaultV2SearchConditions };
  V2_SEARCH_TEXT_KEYS.forEach((key) => {
    next[key] = sp.get(key) ?? "";
  });
  // getAll() の配列は SWR のキーで参照比較されるため、通常の配列に詰め替える（法人検索と同じ）
  V2_SEARCH_ARRAY_KEYS.forEach((key) => {
    next[key] = [...sp.getAll(key)].filter((value) => value !== "");
  });
  V2_SEARCH_FLAG_KEYS.forEach((key) => {
    next[key] = sp.get(key) === "1";
  });
  return next;
};

/**
 * 検索条件をURLクエリにする。前後の空白を除き、空の条件は載せない。日付は "YYYY-MM-DD" にそろえる
 */
export const conditionsToParams = (
  values: V2SearchConditions
): URLSearchParams => {
  const sp = new URLSearchParams();
  const dateEdges = new Map<string, "start" | "end">(
    DATE_RANGE_KEYS.flatMap(([from, to]) => [
      [from, "start"],
      [to, "end"],
    ])
  );
  V2_SEARCH_TEXT_KEYS.forEach((key) => {
    const value = values[key].trim();
    if (value === "") return;
    const edge = dateEdges.get(key);
    sp.set(key, edge ? parseJapaneseDate(value, edge) ?? value : value);
  });
  V2_SEARCH_ARRAY_KEYS.forEach((key) => {
    values[key].forEach((entry) => {
      if (entry.trim()) sp.append(key, entry.trim());
    });
  });
  V2_SEARCH_FLAG_KEYS.forEach((key) => {
    if (values[key]) sp.set(key, "1");
  });
  return sp;
};

const isSort = (value: string | null): value is V2SearchSort =>
  V2_SEARCH_SORTS.some((sort) => sort.value === value);

const isPageSize = (value: number): value is V2SearchPageSize =>
  V2_SEARCH_PAGE_SIZES.some((size) => size === value);

export const paramsToListState = (sp: URLSearchParams): V2SearchListState => {
  const page = Number(sp.get("page"));
  const pageSize = Number(sp.get("pageSize"));
  const sort = sp.get("sort");
  return {
    sort: isSort(sort) ? sort : V2_SEARCH_DEFAULT_SORT,
    page: Number.isInteger(page) && page >= 1 ? page : 1,
    pageSize: isPageSize(pageSize) ? pageSize : V2_SEARCH_DEFAULT_PAGE_SIZE,
    onePerProperty: sp.get("onePerProperty") === "1",
  };
};

/** 一覧の状態をクエリに重ねる（既定値は載せない） */
export const withListState = (
  sp: URLSearchParams,
  state: V2SearchListState
): URLSearchParams => {
  const next = new URLSearchParams(sp);
  LIST_STATE_KEYS.forEach((key) => {
    next.delete(key);
  });
  if (state.sort !== V2_SEARCH_DEFAULT_SORT) next.set("sort", state.sort);
  if (state.page !== 1) next.set("page", String(state.page));
  if (state.pageSize !== V2_SEARCH_DEFAULT_PAGE_SIZE)
    next.set("pageSize", String(state.pageSize));
  if (state.onePerProperty) next.set("onePerProperty", "1");
  return next;
};

const text = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
};

const list = <T>(values: T[]): T[] | undefined =>
  values.length > 0 ? values : undefined;

const tri = (value: string): boolean | undefined =>
  value === "yes" ? true : value === "no" ? false : undefined;

const flag = (value: boolean): true | undefined => (value ? true : undefined);

const finite = (value: string, scale = 1): number | null => {
  const parsed = parseNumberInput(value);
  return parsed == null || Number.isNaN(parsed)
    ? null
    : Math.round(parsed * scale * 1e6) / 1e6;
};

const range = (
  min: string,
  max: string,
  scale = 1
): NumberRange | undefined => {
  const next = { min: finite(min, scale), max: finite(max, scale) };
  return next.min == null && next.max == null ? undefined : next;
};

const dates = (from: string, to: string): DateRange | undefined => {
  const next = {
    from: parseJapaneseDate(from, "start"),
    to: parseJapaneseDate(to, "end"),
  };
  return next.from == null && next.to == null ? undefined : next;
};

const later = (a: string | null, b: string | null): string | null =>
  a == null ? b : b == null ? a : a > b ? a : b;
const earlier = (a: string | null, b: string | null): string | null =>
  a == null ? b : b == null ? a : a < b ? a : b;

/** 新築年月日の範囲と築年数（築N年以上・以内）を、狭いほうに合わせた1つの範囲にする */
export const constructionDates = (
  values: V2SearchConditions,
  today: Date
): DateRange | undefined => {
  const byDate = dates(values.builtFrom, values.builtTo);
  const ageMin = finite(values.ageMin);
  const ageMax = finite(values.ageMax);
  const from = later(
    byDate?.from ?? null,
    ageMax == null ? null : yearsBefore(ageMax, today)
  );
  const to = earlier(
    byDate?.to ?? null,
    ageMin == null ? null : yearsBefore(ageMin, today)
  );
  return from == null && to == null ? undefined : { from, to };
};

const compact = <T extends object>(value: T): T | undefined =>
  Object.values(value).some((entry) => entry !== undefined) ? value : undefined;

const toInts = (values: string[]): number[] | undefined =>
  list(values.map(Number).filter((value) => Number.isInteger(value)));

/** 市区町村の値 "{都道府県コード}:{PrefecturesCity.id}" */
export const cityValue = (prefectureId: number, cityId: number): string =>
  `${prefectureId}:${cityId}`;

const parseCity = (
  value: string
): { prefectureId: number; cityId: number } | null => {
  const match = /^(\d+):(\d+)$/.exec(value);
  return match
    ? { prefectureId: Number(match[1]), cityId: Number(match[2]) }
    : null;
};

/**
 * 都道府県・市区町村の条件。市区町村を選んだ都道府県は市区町村だけで絞り、
 * それ以外の都道府県は県全体で絞る（API では両者が OR）
 */
const regions = (
  values: V2SearchConditions
): { prefectureIds?: number[]; prefectureCityIds?: number[] } => {
  const prefectureIds = toInts(values.prefectures) ?? [];
  const cities = values.cities
    .map(parseCity)
    .filter(
      (city): city is { prefectureId: number; cityId: number } =>
        city != null && prefectureIds.includes(city.prefectureId)
    );
  const withCity = new Set(cities.map((city) => city.prefectureId));
  return {
    prefectureIds: list(prefectureIds.filter((id) => !withCity.has(id))),
    prefectureCityIds: list(cities.map((city) => city.cityId)),
  };
};

/**
 * 検索条件を検索APIの要求にする（検索 API の契約）。空の条件は送らない
 * @param today 築年数を日付に直す基準日
 */
export const toV2SearchRequest = (
  values: V2SearchConditions,
  listState: V2SearchListState,
  today: Date = new Date()
): V2SearchRequest => {
  const ownerNumber = digitsOnly(values.ownerNumber);
  const requestId = Number(normalizeInput(values.requestId));
  const securityAbsent = values.securityExists === "no";
  return {
    property: compact({
      kinds: list(values.kinds),
      ...regions(values),
      location: text(values.location),
      locationIncludeHistory: flag(values.locationHistory),
      number: text(normalizeInput(values.number)),
      realEstateNumber: text(digitsOnly(values.realEstateNumber)),
      buildingName: text(values.buildingName),
      landCategories: list(values.landCategories),
      landAreaM2: range(values.landAreaMin, values.landAreaMax),
      landAreaSources: list(values.landAreaSources),
      buildingTypes: list(values.buildingTypes),
      structureCategories: list(values.structures),
      floorAreaM2: range(values.floorAreaMin, values.floorAreaMax),
      newConstructionDate: constructionDates(values, today),
      newConstructionDateStatuses: list(values.builtStatuses),
      aboveGroundFloors: range(values.floorsMin, values.floorsMax),
      closed:
        values.closed === "exclude" || values.closed === "only"
          ? values.closed
          : undefined,
      oneBuildingFloorAreaM2: range(
        values.oneBuildingFloorAreaMin,
        values.oneBuildingFloorAreaMax
      ),
      unitFloor: range(values.unitFloorMin, values.unitFloorMax),
      hasAnnex: tri(values.hasAnnex),
      floorAreaWithAnnexM2: range(
        values.annexFloorAreaMin,
        values.annexFloorAreaMax
      ),
      titleOwnerName: text(values.titleOwnerName),
      titleOwnerAddress: text(values.titleOwnerAddress),
      siteLocation: text(values.siteLocation),
      siteRightKinds: list(values.siteRightKinds),
    }),
    owner: compact({
      name: text(values.ownerName),
      address: text(values.ownerAddress),
      includeHistory: flag(values.ownerHistory),
      isCorporation: tri(values.ownerCorporation),
      corporateNumber: ownerNumber.length === 13 ? ownerNumber : undefined,
      companyRegistrationNumber:
        ownerNumber.length === 12 ? ownerNumber : undefined,
      count: range(values.ownerCountMin, values.ownerCountMax),
      cautions: list(values.ownerCautions),
      hasExternalCharacter: tri(values.ownerExternalCharacter),
    }),
    koku: compact({
      dispositionRestrictions: list(values.restrictions),
      restrictionsIncludeHistory: flag(values.restrictionsHistory),
      purposeRightKinds: list(values.kokuPurposeKinds),
      purposeActions: list(values.kokuPurposeActions),
      reasonWords: list(values.kokuReasons),
      acceptanceDate: dates(values.kokuAcceptFrom, values.kokuAcceptTo),
      reasonDate: dates(values.kokuReasonDateFrom, values.kokuReasonDateTo),
      hasProvisional: tri(values.kokuProvisional),
      trustPartyName: text(values.trustPartyName),
    }),
    security: compact(
      securityAbsent
        ? {
            exists: false,
            usageRightHolderName: text(values.usageRightHolderName),
          }
        : {
            exists: tri(values.securityExists),
            includeHistory: flag(values.securityHistory),
            holderName: text(values.holderName),
            debtorName: text(values.debtorName),
            debtorAddress: text(values.debtorAddress),
            rightKinds: list(values.securityKinds),
            acceptanceDate: dates(
              values.securityAcceptFrom,
              values.securityAcceptTo
            ),
            // 画面は万円で入力し、APIには円で送る
            currentAmountYen: range(values.amountMin, values.amountMax, 10000),
            reasonWords: list(values.otsukuReasons),
            effectiveRank: range(values.rankMin, values.rankMax),
            handlingBranch: text(values.handlingBranch),
            isProvisional: tri(values.securityProvisional),
            principalFixed: tri(values.principalFixed),
            hasChanges: tri(values.hasChanges),
            jointSecurityPropertiesUndetermined: tri(values.jointUndetermined),
            hasJointSecurity: tri(values.hasJointSecurity),
            jointCatalogNumber: text(values.jointCatalogNumber),
            jointItemLocation: text(values.jointItemLocation),
            jointItemNumber: text(normalizeInput(values.jointItemNumber)),
            usageRightHolderName: text(values.usageRightHolderName),
          }
    ),
    acquisition: compact({
      pictureAcquiredAt: dates(values.acquiredFrom, values.acquiredTo),
      requestName: text(values.requestName),
      requestId:
        Number.isInteger(requestId) && requestId > 0 ? requestId : undefined,
      mineOnly: flag(values.mineOnly),
    }),
    judgement: compact({
      ownerStatuses: list(values.ownerStatuses),
      securityStatuses: list(values.securityStatuses),
    }),
    onePerProperty: listState.onePerProperty,
    sort: listState.sort,
    page: listState.page,
    pageSize: listState.pageSize,
  };
};

/** 条件（並び・ページ以外）が1つでもあるか */
export const hasV2SearchCondition = (values: V2SearchConditions): boolean =>
  V2_SEARCH_TEXT_KEYS.some((key) => values[key].trim() !== "") ||
  V2_SEARCH_ARRAY_KEYS.some((key) => values[key].length > 0) ||
  V2_SEARCH_FLAG_KEYS.some((key) => values[key]);
